import React, { useMemo, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { IconGripVertical } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getContrastColor, stringToColor, COLOR_INFO, COLOR_PRIMARY } from '../../../../shared/colorUtils';
import { LOOSE_DROP_ID, quantityDragId, unitDragId } from './packingActions';

const PAGE_SIZE = 50;

const matches = (q, ...values) => !q || values.some((v) => (v || '').toLowerCase().includes(q));

// Flat list of rows: a group header per product ("X of Y left"), its units, then "product × remaining" lines.
const buildRows = (looseUnits, looseQuantities, q) => {
  const rows = [];
  looseUnits.forEach((group) => {
    const units = group.units.filter((u) => matches(q, group.product_name, u.serial_number, u.qr_code));
    if (!units.length && !matches(q, group.product_name)) return;
    rows.push({ kind: 'group', key: `grp-${group.product_id}`, group });
    units.forEach((unit) => rows.push({ kind: 'unit', key: unitDragId(unit.id), unit, name: group.product_name }));
  });
  looseQuantities
    .filter((line) => matches(q, line.product_name))
    .forEach((line) => rows.push({ kind: 'qty', key: quantityDragId(line.project_equipment), line }));
  return rows;
};

const GroupRow = ({ group }) => {
  const { t } = useTranslation();
  const color = stringToColor(group.product_name);
  return (
    <tr className="table-active" style={{ userSelect: 'none' }}>
      <td colSpan={4} className="py-1 ps-3" style={{ border: 0 }}>
        <span className="fw-semibold text-muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {group.product_name}
        </span>
        <Badge className="ms-2 fw-normal" style={{ fontSize: '0.65rem', background: color, color: getContrastColor(color) }}>
          {t('projects.cases.remainingOf', { remaining: group.remaining, total: group.total })}
        </Badge>
      </td>
    </tr>
  );
};

const DraggableRow = ({ row, index }) => {
  const { t } = useTranslation();
  const isUnit = row.kind === 'unit';
  const badgeColor = isUnit ? COLOR_PRIMARY : COLOR_INFO;
  return (
    <Draggable draggableId={row.key} index={index}>
      {(drag, snapshot) => (
        <tr
          ref={drag.innerRef}
          {...drag.draggableProps}
          style={{ background: snapshot.isDragging ? 'var(--tblr-azure-lt)' : undefined, ...drag.draggableProps.style }}
        >
          <td style={{ width: 32 }}>
            <span {...drag.dragHandleProps} className="text-muted d-flex" style={{ cursor: 'grab', lineHeight: 1 }}>
              <IconGripVertical size={14} />
            </span>
          </td>
          <td>
            <span className="fw-medium small">{isUnit ? row.name : row.line.product_name}</span>
            {isUnit && row.unit.serial_number && <span className="text-muted ms-2 small">· {row.unit.serial_number}</span>}
          </td>
          <td style={{ width: 110 }}>
            <Badge className="fw-normal" style={{ background: badgeColor, color: getContrastColor(badgeColor) }}>
              {isUnit ? t('projects.cases.typeUnit') : `×${row.line.remaining}`}
            </Badge>
          </td>
          <td><code className="small text-muted">{isUnit ? row.unit.qr_code : '—'}</code></td>
        </tr>
      )}
    </Draggable>
  );
};

/** Units and "product × quantity" of the project that are not packed yet; rows are dragged into cases. */
const LooseItemsTable = ({ looseUnits = [], looseQuantities = [] }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const q = search.trim().toLowerCase();
  const rows = useMemo(() => buildRows(looseUnits, looseQuantities, q), [looseUnits, looseQuantities, q]);
  const remaining = looseUnits.reduce((sum, g) => sum + g.remaining, 0)
    + looseQuantities.reduce((sum, l) => sum + l.remaining, 0);
  let dragIndex = 0;

  return (
    <div className="mb-3 rounded-3 border overflow-hidden">
      <div className="px-3 py-2 border-bottom d-flex align-items-center gap-2">
        <strong>{t('projects.cases.looseItems')}</strong>
        <Badge bg="secondary">{remaining}</Badge>
        <div className="ms-auto" style={{ minWidth: 220 }}>
          <Form.Control
            size="sm"
            type="search"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisible(PAGE_SIZE); }}
          />
        </div>
      </div>
      <div style={{ maxHeight: 460, overflowY: 'auto' }}>
        <table className="table table-hover table-vcenter table-sm mb-0">
          <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--tblr-bg-surface)' }}>
            <tr>
              <th style={{ width: 32 }}></th>
              <th>{t('projects.items.name')}</th>
              <th style={{ width: 110 }}>{t('projects.cases.type')}</th>
              <th>{t('warehouses.units.qrCode')}</th>
            </tr>
          </thead>
          <Droppable droppableId={LOOSE_DROP_ID}>
            {(provided, snapshot) => (
              <tbody
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{ background: snapshot.isDraggingOver ? 'var(--tblr-warning-lt)' : undefined }}
              >
                {rows.slice(0, visible).map((row) => (row.kind === 'group'
                  ? <GroupRow key={row.key} group={row.group} />
                  : <DraggableRow key={row.key} row={row} index={dragIndex++} />))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className={`small fst-italic py-4 text-center ${remaining ? 'text-muted' : 'text-success'}`}>
                      {remaining ? t('common.noResults') : t('projects.cases.allItemsPacked')}
                    </td>
                  </tr>
                )}
                <tr style={{ display: 'none' }}><td>{provided.placeholder}</td></tr>
              </tbody>
            )}
          </Droppable>
        </table>
        {rows.length > visible && (
          <div className="text-center py-2">
            <Button size="sm" variant="link" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
              {t('projects.cases.showMore')} ({visible} / {rows.length})
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LooseItemsTable;
